import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import Icon, { IconType } from 'components/Icon';
import MarkdownText from 'components/Widget/MarkdownText';
import Pill from 'components/Widget/Pill';
import Thumbnail, { ThumbnailSize } from 'components/Widget/Thumbnail';
import { dsPickToNavTarget } from 'designSystem/content/util';
import { Attachment, DsPick } from 'dragonmouse-shared/interfaces';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import { addDsTab, setActiveDsTab } from 'redux/mode/actions';
import * as routes from 'routes';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';
import * as S from './styles';

interface ResolvedDsPick {
	pick: DsPick;
	name: string;
}

interface UserMessageProps {
	message: string;
	isMobile?: boolean;
	attachments?: Attachment[];
	sourceFrameIds?: string[];
	resolvedDsPicks?: ResolvedDsPick[];
	onContextMenu?: (e: React.MouseEvent) => void;
}

const UserMessage = ({
	message,
	isMobile,
	attachments,
	sourceFrameIds,
	resolvedDsPicks,
	onContextMenu,
}: UserMessageProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isCopied, setIsCopied] = useState(false);

	const { designSystem, dsTabs } = useSelector(
		(r: RootReducer) => ({
			designSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			dsTabs: r.mode.dsTabs,
		}),
		shallowEqual
	);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onPickClick = (pick: DsPick, name: string) => {
		const target = dsPickToNavTarget(pick);
		if (!target || !designSystem) return;

		const tabId = target.contentPath ? `${target.section}-${target.contentPath}` : `section-${target.section}`;
		const existingTab = dsTabs.find((t) => t.id === tabId);
		if (existingTab) {
			dispatch(setActiveDsTab(tabId));
		} else {
			dispatch(
				addDsTab({
					id: tabId,
					section: target.section,
					contentPath: target.contentPath,
					label: name,
					showBackButton: !!target.contentPath,
				})
			);
		}

		const url = routes.designSystem(designSystem.id, designSystem.title);
		const params = new URLSearchParams({ section: target.section });
		if (target.contentPath) params.set('contentPath', target.contentPath);
		navigate(`${url}?${params.toString()}`);
	};

	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(message);
			setIsCopied(true);
			setTimeout(() => setIsCopied(false), 1500);
		} catch {
			toast.error('Failed to copy');
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderDsPicks = () => {
		if (!resolvedDsPicks?.length) return null;
		return (
			<S.DsPicks>
				{resolvedDsPicks.map(({ pick, name }) => (
					<Pill
						key={getReactKey(pick)}
						label={name}
						tooltip="Click to open in design system"
						onClick={() => onPickClick(pick, name)}
					/>
				))}
			</S.DsPicks>
		);
	};

	const renderAttachments = () => {
		if (!attachments?.length) return null;
		return (
			<S.Attachments
				onWheel={(e: React.WheelEvent) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{attachments.map((attachment) => (
					<ChatAttachmentTile
						key={attachment.storagePath}
						attachment={attachment}
						state={ChatAttachmentTileState.REGULAR}
					/>
				))}
			</S.Attachments>
		);
	};

	const renderThumbnails = () => {
		if (!sourceFrameIds?.length) return null;
		return (
			<S.Thumbnails
				onWheel={(e: React.WheelEvent) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{sourceFrameIds.map((frameId) => (
					<Thumbnail
						key={frameId}
						frameId={frameId}
						size={ThumbnailSize.CHAT}
						data-frame-id={frameId}
						selectOnClick
					/>
				))}
			</S.Thumbnails>
		);
	};

	return (
		<S.Base className="user-message" isMobile={isMobile}>
			<S.Bubble isMobile={isMobile} onContextMenu={onContextMenu}>
				{renderDsPicks()}
				{renderAttachments()}
				{renderThumbnails()}
				<MarkdownText onContextMenu={onContextMenu}>{message}</MarkdownText>
			</S.Bubble>
			<S.ActionBar>
				<S.CopyButton onClick={onCopy}>
					<Icon
						type={isCopied ? IconType.CHECK : IconType.COPY}
						width={12}
						height={12}
						color={isCopied ? theme.gray[1000] : theme.gray[600]}
					/>
					{isCopied ? 'Copied' : ''}
				</S.CopyButton>
			</S.ActionBar>
		</S.Base>
	);
};

// Stable key from DsPick for rendering
function getReactKey(pick: DsPick): string {
	if (pick.kind === 'token') return `token-${pick.cssVariable}`;
	if (pick.kind === 'recipe') return `recipe-${pick.className}`;
	if (pick.kind === 'component') return `comp-${pick.tagName}`;
	if (pick.kind === 'guideline') return `guideline-${pick.guidelinePath}`;
	if (pick.kind === 'gallery') return `gallery-${pick.galleryPath}`;
	if (pick.kind === 'section') return `section-${pick.section}`;
	return `asset-${pick.assetPath}`;
}

export default UserMessage;
